import { detectLanguage, expectedDetectionFor, normalizeLanguageCode } from "./language";

export class TranslationError extends Error {
  reason: "quota" | "network" | "unavailable";

  constructor(message: string, reason: "quota" | "network" | "unavailable") {
    super(message);
    this.name = "TranslationError";
    this.reason = reason;
  }
}

const REQUEST_TIMEOUT_MS = 8000;

// Uses the free, keyless endpoint behind translate.google.com (client=gtx).
// It's undocumented: Google can rate-limit or block it without notice, so
// every failure surfaces as a TranslationError and the original text stays.
// Called straight from the browser (it allows CORS), so requests are spread
// across visitors' IPs rather than all coming from our server.
const GOOGLE_TRANSLATE_ENDPOINT = "https://translate.googleapis.com/translate_a/single";

export async function translateText(text: string, targetLang: string, sourceLang: string = "auto"): Promise<string> {
  // Already in the requested language - nothing to translate (sending it
  // anyway is what garbled Tamil quotes: "translating" Tamil as if English).
  if (targetLang === sourceLang) return text;

  // sl=auto: Google detects the source itself, which covers every language
  // (our stored quotes.language only distinguishes scripts, e.g. it can't
  // tell French from English).
  const endpoint =
    `${GOOGLE_TRANSLATE_ENDPOINT}?client=gtx&sl=auto&tl=${encodeURIComponent(targetLang)}` +
    `&dt=t&q=${encodeURIComponent(text)}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(endpoint, { signal: controller.signal });
  } catch {
    throw new TranslationError("Couldn't reach the translation service. Check your connection and try again.", "network");
  } finally {
    clearTimeout(timeoutId);
  }

  // Rate limiting shows up as 429 or an HTML "unusual traffic" page.
  if (response.status === 429) {
    throw new TranslationError("Translation limit reached for now. Please try again later.", "quota");
  }
  const data = await response.json().catch(() => null);
  if (!response.ok || !Array.isArray(data)) {
    throw new TranslationError("Translation isn't available right now. Please try again later.", "unavailable");
  }

  // Response shape: [[[translatedSegment, originalSegment, ...], ...], null, detectedSourceLang, ...]
  const detectedSource = typeof data[2] === "string" ? normalizeLanguageCode(data[2]) : null;
  // Validation: Google says the text is already in the target language, so
  // leave it exactly as written.
  if (detectedSource && detectedSource === normalizeLanguageCode(targetLang)) return text;

  const translated = Array.isArray(data[0])
    ? data[0].map((segment: unknown) => (Array.isArray(segment) && typeof segment[0] === "string" ? segment[0] : "")).join("")
    : "";
  if (!translated.trim()) {
    throw new TranslationError("Translation isn't available for this language right now.", "unavailable");
  }

  // Validation: reject results that aren't actually in the target language's
  // script (where we can check it) instead of showing garbled text.
  const expected = expectedDetectionFor(targetLang);
  if (expected && detectLanguage(translated) !== expected) {
    throw new TranslationError("Couldn't translate this quote into that language. Showing the original.", "unavailable");
  }

  return translated;
}
